import React from "react";
import { Calendar, Download } from "lucide-react";
import { DailyProfit } from "../../../../services/profitTrackingService";
import { formatNumber } from "../utils/profitFormatters";

interface DailyTabProps {
  dailyProfits: DailyProfit[];
  onDownload: (type: "entries" | "country" | "payment" | "daily") => void;
}

export const DailyTab: React.FC<DailyTabProps> = ({ dailyProfits, onDownload }) => {
  return (
    <div className="p-6 bg-white rounded-xl border">
      <div className="flex justify-between items-center mb-4">
        <h2 className="flex gap-2 items-center text-lg font-semibold text-gray-800">
          <Calendar size={20} className="text-indigo-600" /> Gain Journalier par Moyen de Paiement
        </h2>
        <button
          onClick={() => onDownload("daily")}
          className="flex gap-1 items-center px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg border hover:bg-gray-200"
        >
          <Download size={14} /> Excel
        </button>
      </div>
      {dailyProfits.length === 0 ? (
        <p className="text-sm text-gray-400">Aucune donnée</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-4 py-3 font-medium text-left text-gray-600">Date</th>
                <th className="px-4 py-3 font-medium text-left text-gray-600">Pays</th>
                <th className="px-4 py-3 font-medium text-left text-gray-600">Moyen de paiement</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Capital du jour</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu Brut</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Frais</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu Net</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Revenu (CAD)</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Dépenses (CAD)</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Bén. Final</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Bén. Final (CAD)</th>
                <th className="px-4 py-3 font-medium text-right text-gray-600">Nb Ops</th>
              </tr>
            </thead>
            <tbody>
              {dailyProfits.map((d, i) => {
                const revenueCad = d.daily_profit_cad || 0;
                const expensesCad = d.daily_expenses_cad ?? d.daily_expenses ?? 0;
                // Bén. Final (CAD) = Revenu (CAD) − Dépenses (CAD)
                const finalCad = parseFloat((revenueCad - expensesCad).toFixed(4));
                return (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{d.date}</td>
                    <td className="px-4 py-3">{d.country}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full">
                        {d.payment_method}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">{formatNumber(d.daily_capital)}</td>
                    <td className="px-4 py-3 text-right text-green-600">{formatNumber(d.daily_gross_profit)}</td>
                    <td className="px-4 py-3 text-right text-red-500">{formatNumber(d.daily_fees)}</td>
                    <td className="px-4 py-3 font-semibold text-right text-emerald-600">
                      {formatNumber(d.daily_net_profit)}
                    </td>
                    <td className="px-4 py-3 font-semibold text-right text-purple-600">{formatNumber(revenueCad)}</td>
                    <td className="px-4 py-3 text-right text-orange-500">{formatNumber(expensesCad)}</td>
                    <td className="px-4 py-3 font-bold text-right text-emerald-700">
                      {formatNumber(d.daily_final_profit)}
                    </td>
                    <td
                      className="px-4 py-3 font-bold text-right text-purple-700"
                      title={`${formatNumber(revenueCad)} − ${formatNumber(expensesCad)}`}
                    >
                      {formatNumber(finalCad)}
                    </td>
                    <td className="px-4 py-3 text-right">{d.operation_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
